import React from 'react';

import { Callout, Step } from './shared';

const Transfer: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-4">Project Transfer</h2>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      Copy a whole project from one platform to another — Jira, Trello, or Standalone, in any direction. This is a
      one-time copy, not a sync: the source project is never modified.
    </p>

    <div className="space-y-3 mb-6">
      <Step n={1}>
        Open the project you want to copy and click <strong>Transfer…</strong>, or go to <strong>Transfers</strong> in
        the sidebar to see past transfers.
      </Step>
      <Step n={2}>
        Choose the target platform and, optionally, a name for the new project. Only platforms you&apos;re actually
        connected to are offered — Standalone is always available since it needs no connection. If nothing else is
        connected, a <strong>Go to integrations</strong> button takes you straight there.
      </Step>
      <Step n={3}>
        MFlow reads the source project and proposes a status mapping (e.g. Jira&apos;s &quot;In Review&quot; →
        Standalone&apos;s &quot;In Progress&quot;). Review and adjust it — this only needs a handful of decisions even
        for projects with hundreds of tasks, since it maps distinct statuses, not individual tasks.
      </Step>
      <Step n={4}>
        Confirm to start the copy. MFlow first creates the target project/board itself — you&apos;ll see a
        &quot;Creating the target project/board…&quot; status while that&apos;s in progress — then each task&apos;s
        creation progress streams in live; failed items can be retried individually.
      </Step>
    </div>

    <Callout type="warning">
      When transferring <strong>into</strong> Jira, a task&apos;s status can&apos;t be set at creation — MFlow moves it
      afterward. If the target project&apos;s workflow doesn&apos;t match the assumed default statuses, that move can
      silently fail; check the transferred tasks&apos; statuses in Jira afterward if this matters to you.
    </Callout>

    <h3 className="font-semibold mb-2 mt-6">What doesn&apos;t carry over</h3>
    <p className="text-sm text-base-content/70 mb-2 leading-relaxed">
      Title, description, and due dates always transfer. A few things intentionally don&apos;t, since they either
      don&apos;t exist on every platform or don&apos;t map cleanly:
    </p>
    <ul className="text-sm text-base-content/70 list-disc pl-5 space-y-1">
      <li>Assignees</li>
      <li>Priority — only Standalone tracks it, so it&apos;s dropped transferring to/from Jira or Trello</li>
      <li>Trello labels, attachments, and comments</li>
      <li>Jira subtask hierarchy — subtasks land as flat top-level tasks on the target</li>
    </ul>
  </div>
);

export default Transfer;
